import { useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/config";
import { trackAuthFailure } from "@/services/errorTracking";
import { toast } from "sonner";

// ============ Refresh Token Queue Mechanism ============
// Prevents race conditions when multiple 401s trigger concurrent refresh attempts
// Kept module-scoped to act as singleton
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeToRefresh = (callback) => {
    refreshSubscribers.push(callback);
};

const onRefreshed = (token) => {
    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];
};

const onRefreshFailed = (error) => {
    refreshSubscribers.forEach(callback => callback(null, error));
    refreshSubscribers = [];
};

export const useAxiosSetup = ({ login, logout, userId, token }) => {
    // We use refs to avoid interceptor being recreated constantly, 
    // but the interceptor needs access to current login/logout functions
    const authRef = useRef({ login, logout, userId, token });

    useEffect(() => {
        authRef.current = { login, logout, userId, token };
    }, [login, logout, userId, token]);

    useEffect(() => {
        // Ensure cookies are sent with requests
        axios.defaults.withCredentials = true;

        // Add axios interceptor to handle 401 Unauthorized globally
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                const currentAuth = authRef.current;

                // If 401 and not already retried
                if (error.response && error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    // Don't refresh if we are on login page (failed login attempt)
                    if (window.location.pathname === '/login') {
                        return Promise.reject(error);
                    }

                    // Stop infinite loop: if the error comes from the refresh endpoint, don't try to refresh again
                    if (originalRequest.url.includes('/auth/refresh')) {
                        return Promise.reject(error);
                    }

                    // If a refresh is already in progress, wait for it instead of making another call
                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            subscribeToRefresh((newToken, err) => {
                                if (err) {
                                    reject(err);
                                } else if (newToken) {
                                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                                    resolve(axios(originalRequest));
                                } else {
                                    reject(new Error('Refresh failed'));
                                }
                            });
                        });
                    }

                    // This is the first 401 - initiate refresh
                    isRefreshing = true;

                    try {
                        // Attempt to refresh token
                        const res = await axios.post(`${API}/auth/refresh`);

                        if (res.data.token) {
                            // Update local state and sync service
                            currentAuth.login(res.data.token, res.data.user_id || currentAuth.userId);

                            // Notify all waiting requests that refresh succeeded
                            onRefreshed(res.data.token);

                            // Update authorization header and retry
                            originalRequest.headers['Authorization'] = `Bearer ${res.data.token}`;
                            return axios(originalRequest);
                        }
                    } catch (refreshError) {
                        console.error("Refresh failed:", refreshError);
                        trackAuthFailure('token_refresh_failed', { error: refreshError.message });

                        // Notify all waiting requests that refresh failed
                        onRefreshFailed(refreshError);

                        // If refresh fails, logout and redirect
                        toast.error("Session expired. Please login again.");
                        currentAuth.logout('session_expired');
                    } finally {
                        isRefreshing = false;
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []); // Run once on mount
};
